/* eslint-disable no-restricted-syntax */
/* eslint-disable no-nested-ternary */
/* eslint-disable prefer-template */
import React, { useEffect, useMemo, useState } from "react";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import { useLastLiftMessage, useDirectionForLift } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "./IconDefinition";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import {
  RuleCondition,
  RuleExpr,
  RuleMemory,
  RuleDefinition,
  ConditionDef,
} from "../../../interfaces/IconDefinitions";
// UTILS
import { bitmaskToDoorIconFiltered } from "../../../utils/bitmaskToIcon";
import { bitmaskPrefix } from "../../../utils/typedKeys";
import { stringToUuidT } from "../../../utils/uuid";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";

export default function IconDefinitionElevatorCustom({
  floor,
  lift,
  bank,
  floorIndex,
  adapterRules,
}: {
  floor: BankFloorInterface;
  lift: LiftInterface;
  bank: BankInterface;
  floorIndex: number;
  adapterRules: RuleDefinition[];
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */

  // Use adapter rules only when at least one rule has conditions configured,
  // otherwise fall back to empty (bitmask-based icons).
  const rules: RuleDefinition[] = useMemo(() => {
    const hasConfiguredRules =
      adapterRules &&
      adapterRules.length > 0 &&
      adapterRules.some(
        (r) =>
          r.conditions &&
          r.conditions.length > 0 &&
          r.activeWhen?.kind === "group" &&
          r.activeWhen?.children?.length > 0,
      );
    return hasConfiguredRules ? adapterRules : [];
  }, [adapterRules]);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [memory, setMemory] = useState<RuleMemory>({});
  const [ruleIcon, setRuleIcon] = useState<string | null>(null);
  const [activeRule, setActiveRule] = useState<RuleDefinition | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const doorUnknownAsOpening = useSelector(
    (state: RootState) => state.monitoring.doorUnknownAsOpening,
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(lift.id), [lift.id]);
  const lastLiftMessage = useLastLiftMessage(liftUuid);
  const socketMessageLift: SocketMessageInterface = useMemo(
    () => lastLiftMessage || BLANK_SOCKET_MESSAGE,
    [lastLiftMessage],
  );
  const hasFaults: boolean = socketMessageLift.faults;

  const prefix = useMemo(
    () => bitmaskPrefix(socketMessageLift.bankId, socketMessageLift.liftId),
    [socketMessageLift.bankId, socketMessageLift.liftId],
  );

  // Determine if bank has mixed door configuration (both front and rear across all floors)
  const bankHasMixedDoors = useMemo(() => {
    if (!bank.bank_floors || bank.bank_floors.length === 0) return false;

    const hasFrontDoor = bank.bank_floors.some((f) => f.front_door);
    const hasRearDoor = bank.bank_floors.some((f) => f.rear_door);

    return hasFrontDoor && hasRearDoor;
  }, [bank.bank_floors]);

  const doorIcon = useMemo(() => {
    const icon = bitmaskToDoorIconFiltered(
      socketMessageLift.bitmask,
      prefix,
      floor.front_door,
      floor.rear_door,
      doorUnknownAsOpening,
      bankHasMixedDoors,
    );
    return icon;
  }, [
    socketMessageLift.bitmask,
    socketMessageLift.position,
    prefix,
    floor.front_door,
    floor.rear_door,
    floor.floor_index,
    doorUnknownAsOpening,
    bankHasMixedDoors,
  ]);
  const { directionIcon, lastDirectionIcon } = useDirectionForLift(liftUuid);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  function evalExpr(expr: RuleExpr, memory_: RuleMemory): boolean {
    if (expr && expr.kind && expr.kind === "ref") {
      return !!memory_[expr.id];
    }
    if (expr && expr.children && expr.children.length === 0) return false;
    if (expr && expr.op && expr.op === "AND") {
      return expr?.children?.every((child) => evalExpr(child, memory_));
    }
    return expr?.children?.some((child) => evalExpr(child, memory_));
  }

  function updateMemoryWithMessage(
    prev: RuleMemory,
    ruleDefs: RuleDefinition[],
    socketMessageLift_: SocketMessageInterface,
    conditionMatches: (c: RuleCondition, s: SocketMessageInterface) => boolean,
  ): RuleMemory {
    let changed = false;
    const next: RuleMemory = { ...prev };

    for (const rule of ruleDefs) {
      if (rule && rule.conditions) {
        for (const def of rule.conditions) {
          if (!next[def.id] && conditionMatches(def.condition, socketMessageLift_)) {
            next[def.id] = true;
            changed = true;
          }
        }
      }
    }

    return changed ? next : prev;
  }

  function findWinningRule(ruleDefs: RuleDefinition[], memory_: RuleMemory): RuleDefinition | null {
    for (const rule of ruleDefs) {
      if (evalExpr(rule.activeWhen, memory_)) return rule;
    }
    return null;
  }

  function matchesExprOnMessage(
    expr: RuleExpr,
    conditions: ConditionDef[],
    socketMessageLift_: SocketMessageInterface,
    conditionMatches: (c: RuleCondition, s: SocketMessageInterface) => boolean,
  ): boolean {
    const instant: RuleMemory = {};
    for (const def of conditions) {
      if (conditionMatches(def.condition, socketMessageLift_)) {
        instant[def.id] = true;
      }
    }
    return evalExpr(expr, instant);
  }

  const matchIfNotNull = <T,>(ruleValue: T | null, predicate: (v: T) => boolean): boolean => {
    if (ruleValue === null) return true;
    return predicate(ruleValue);
  };

  function extractInnerMessageCodeSafe(content: unknown): number | null {
    if (!content) return null;
    const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
    let cur: unknown = content;
    for (let i = 0; i < 6; i += 1) {
      if (!isObj(cur)) return null;
      const msg = cur.message;
      if (typeof msg === "number") return msg;
      cur = msg;
    }

    return null;
  }

  function socketInnerMessageCode(s: SocketMessageInterface): number | null {
    return extractInnerMessageCodeSafe((s.msg as any)?.content?.message);
  }

  function conditionMatchesSocket(c: RuleCondition, s: SocketMessageInterface): boolean {
    return (
      matchIfNotNull(c.contentType, (v) => s.msg.contentType === v) &&
      matchIfNotNull(c.messageType, (v) => s.msg.messageType === v) &&
      matchIfNotNull(c.message, (v) => socketInnerMessageCode(s) === v) &&
      matchIfNotNull(c.active, (v) => s.msg.active === v) &&
      matchIfNotNull(c.frontDoor, (v) => s.doorStatus.frontOpen === v) &&
      matchIfNotNull(c.rearDoor, (v) => s.doorStatus.rearOpen === v) &&
      matchIfNotNull(c.direction, (v) => s.direction === v)
    );
  }

  const activeFloors = useMemo(() => {
    if (!socketMessageLift) {
      return {
        car: new Set<number>(),
        hallUp: new Set<number>(),
        hallDown: new Set<number>(),
        locks: new Set<number>(),
      };
    }

    const car = new Set([...socketMessageLift.carCalls.front, ...socketMessageLift.carCalls.rear]);

    const hallUp = new Set([
      ...socketMessageLift.hallCalls.upAssigned.front,
      ...socketMessageLift.hallCalls.upAssigned.rear,
    ]);

    const hallDown = new Set([
      ...socketMessageLift.hallCalls.downAssigned.front,
      ...socketMessageLift.hallCalls.downAssigned.rear,
    ]);

    const locks = new Set([
      ...socketMessageLift.carCalls.frontLock,
      ...socketMessageLift.carCalls.rearLock,
      ...socketMessageLift.hallCalls.upAssigned.frontLock,
      ...socketMessageLift.hallCalls.upAssigned.rearLock,
      ...socketMessageLift.hallCalls.downAssigned.frontLock,
      ...socketMessageLift.hallCalls.downAssigned.rearLock,
    ]);

    return { car, hallUp, hallDown, locks };
  }, [socketMessageLift]);

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (!socketMessageLift) return;
    if (rules.length === 0) {
      setRuleIcon(null);
      setActiveRule(null);
      setMemory({});
      return;
    }
    if (activeRule) {
      const shouldRemove = matchesExprOnMessage(
        activeRule.removeWhen,
        activeRule.conditions,
        socketMessageLift,
        conditionMatchesSocket,
      );

      if (shouldRemove) {
        setActiveRule(null);
        setRuleIcon(null);
        setMemory({});
        return;
      }
      const replacer = rules.find((rule) => {
        if (rule.id === activeRule.id) return false;
        return matchesExprOnMessage(
          rule.activeWhen,
          rule.conditions,
          socketMessageLift,
          conditionMatchesSocket,
        );
      });
      if (replacer) {
        setActiveRule(replacer);
        setRuleIcon((prev) => (prev === replacer.icon ? prev : replacer.icon));
        setMemory({});
        return;
      }
      setRuleIcon((prev) => (prev === activeRule.icon ? prev : activeRule.icon));
      return;
    }

    const nextMemory = updateMemoryWithMessage(
      memory,
      rules,
      socketMessageLift,
      conditionMatchesSocket,
    );
    const winner = findWinningRule(rules, nextMemory);
    if (winner) {
      setActiveRule(winner);
      setRuleIcon(winner.icon);
      setMemory({});
    } else {
      setMemory(nextMemory);
      setRuleIcon(null);
    }
  }, [socketMessageLift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const getIcon = () => {
    if (!socketMessageLift) return null;

    if (floor.floor_index === socketMessageLift.position) {
      if (socketMessageLift.position < 0) {
        return (
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType="ELEVATOR"
            icon="elWarning"
            iconSize={20}
          />
        );
      }
      if (ruleIcon) {
        return (
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType="ELEVATOR"
            icon={ruleIcon}
            iconSize={20}
          />
        );
      }
      const displayDirection = directionIcon || lastDirectionIcon;
      const directionDimmed = !directionIcon && !!lastDirectionIcon;

      // If no door icon and no direction, render nothing
      if (!doorIcon && !displayDirection) {
        return null;
      }

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {doorIcon && (
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType="ELEVATOR"
              icon={doorIcon}
              iconSize={20}
            />
          )}
          {displayDirection && (
            <span style={{ opacity: directionDimmed ? 0.3 : 1 }}>
              <IconDefinition
                isMap={false}
                expanded={false}
                liftType="ELEVATOR"
                icon={displayDirection}
                iconSize={20}
              />
            </span>
          )}
        </div>
      );
    }

    if (activeFloors.locks.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elLocked"
          iconSize={20}
        />
      );
    }

    if (activeFloors.hallDown.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoalRed"
          iconSize={20}
        />
      );
    }

    if (activeFloors.hallUp.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoalGreen"
          iconSize={20}
        />
      );
    }

    if (activeFloors.car.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoal"
          iconSize={20}
        />
      );
    }

    return null;
  };

  const containerClass =
    `monitoring__card__liftContainer__${theme} ` +
    (hasFaults ? "monitoringView__dangerElevator " : "") +
    (floorIndex === 0 ? (hasFaults ? "monitoringView__dangerElevator__top " : "") : "") +
    (floorIndex === bank.bank_floors.length - 1
      ? hasFaults
        ? "monitoringView__dangerElevator__bottom "
        : ""
      : "") +
    (floorIndex === bank.bank_floors.length - 1
      ? " monitoring__card__liftContainerBorderBottom  "
      : "") +
    (floorIndex === 0 ? "monitoring__card__liftContainerBorderTop" : "");

  return <div className={containerClass}>{getIcon()}</div>;
}
